import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WebsocketService } from '../../services/websocket.service';
import { ButtonCardComponent } from '../button-card/button-card.component';
import { TranslatePipe } from '@ngx-translate/core';
import { LocalizedDatePipe } from '../../pipes/localized-date.pipe';

/** The scenario currently running, resolved from a live SCENARIOEVENT + the scenario config. */
interface RunningView {
  scenarioName: string;
  phaseLabel: string;     // the zone/output being watered right now
  stepIndex: number;      // 1-based position among the scenario's wait steps
  totalSteps: number;
  phaseEndTime: number;   // epoch ms the current phase ends
  endTime: number;        // epoch ms the whole scenario ends
}

/**
 * Irrigation theme: what a watering controller is doing right now (running scenario phase +
 * countdown), the next scheduled run, a "skip today" switch, and the buttons that trigger
 * scenarios. Everything is derived from data the controller already reports — SCENARIOEVENT
 * for progress and config.tasks for the schedule — so no new telemetry is needed.
 */
@Component({
  selector: 'app-irrigation-dashboard',
  standalone: true,
  imports: [TranslatePipe, LocalizedDatePipe, CommonModule, ButtonCardComponent],
  templateUrl: './irrigation-dashboard.component.html',
  styleUrl: './irrigation-dashboard.component.scss'
})
export class IrrigationDashboardComponent implements OnChanges, OnDestroy {
  @Input() controller: any;
  @Input() runningEvent: any;                                  // {mac, scenarioId, order, wait} | null
  @Input() buttonsGroups: { [key: string]: any[] } = {};

  running: RunningView | null = null;
  nextRun: { scenarioName: string; when: number } | null = null;
  now = Date.now();
  // Built once per input change, never in a getter: the 1 s ticker runs change detection
  // every second, and fresh wrapper objects would make ngFor rebuild every button card.
  // A rebuilt card loses the isHolding flag mousedown set, so the press is never sent.
  buttonGroupList: Array<{ key: string; value: any[] }> = [];
  private ticker: any;

  constructor(private ws: WebsocketService) {
    this.ticker = setInterval(() => {
      this.now = Date.now();
      if (this.running && this.now >= this.running.endTime) this.running = null;
    }, 1000);
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['runningEvent']) this.computeRunning();
    if (ch['controller']) { this.computeRunning(); this.computeNext(); }
    if (ch['buttonsGroups']) this.buildButtonGroupList();
  }

  ngOnDestroy(): void { clearInterval(this.ticker); }

  get hasButtons(): boolean {
    return Object.values(this.buttonsGroups || {}).some(g => g && g.length > 0);
  }

  private buildButtonGroupList(): void {
    this.buttonGroupList = Object.entries(this.buttonsGroups || {}).map(([key, value]) => ({ key, value }));
  }

  trackByGroupKey(_i: number, g: { key: string }) { return g.key; }
  trackByButton(i: number, b: any) { return b?.eid ?? b?.id ?? i; }

  get skipToday(): boolean {
    return this.controller?.settings?.skipDate === this.todayStr();
  }

  onSkipToggle(ev: Event): void {
    const checked = (ev.target as HTMLInputElement).checked;
    const mac = this.controller?.mac;
    if (!mac) return;
    this.ws.sendMessage({ type: 'COMMAND', payload: { command: checked ? 'SKIPTODAY' : 'RESUMETODAY', mac } });
    // Reflect intent immediately; the backend persists skipDate and forwards to the device.
    if (!this.controller.settings) this.controller.settings = {};
    this.controller.settings.skipDate = checked ? this.todayStr() : null;
    this.computeNext();
  }

  // ---- countdown helpers used by the template ----
  secsLeft(endMs: number): number { return Math.max(0, Math.round((endMs - this.now) / 1000)); }
  fmtDur(s: number): string { const m = Math.floor(s / 60); return `${m}:${String(s % 60).padStart(2, '0')}`; }

  // ---- internals ----
  private scenarios(): any[] { return this.controller?.config?.scenarios || []; }
  private outputs(): any[] { return this.controller?.config?.outputs || []; }
  private tasks(): any[] { return this.controller?.config?.tasks || []; }

  private todayStr(d: Date = new Date()): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private outputName(eid: string): string {
    const o = this.outputs().find((x: any) => x.eid === eid);
    return o?.name || eid?.split('-').pop() || 'output';
  }

  private computeRunning(): void {
    const ev = this.runningEvent;
    // No event means nothing is running. Returning early instead left the last scenario's
    // card on screen after it had ended.
    if (!ev) { this.running = null; return; }
    if (this.controller && ev.mac && ev.mac !== this.controller.mac) return;
    const sc = this.scenarios().find((s: any) => s.id === ev.scenarioId);
    if (!sc) { this.running = null; return; }

    const acts: any[] = sc.actions || [];
    const order = Math.min(ev.order ?? 0, Math.max(0, acts.length - 1));

    // Phase = the last output turned ON at or before the current step: the zone watering now.
    let phaseLabel = sc.name;
    for (let i = order; i >= 0; i--) {
      const a = acts[i];
      if (a?.type === 'output' && a?.action === 'on' && a?.outputEid) { phaseLabel = this.outputName(a.outputEid); break; }
    }

    // Remaining time = sum of the wait steps from here to the end of the scenario.
    let remaining = 0;
    for (let i = order; i < acts.length; i++) if (acts[i]?.type === 'wait') remaining += (acts[i].wait || 0);
    const phaseWait = ev.wait || 0;

    const totalWaitSteps = acts.filter((a: any) => a?.type === 'wait').length;
    const stepIndex = acts.slice(0, order + 1).filter((a: any) => a?.type === 'wait').length;

    // Anchor the countdown to when the phase actually began, not to now. This runs again on
    // every ngOnChanges — including each USERDEVICES refresh — and re-anchoring to now
    // restarted the clock at full duration every time, so it could never reach endTime.
    const anchor = ev.startedAt ?? Date.now();

    this.running = {
      scenarioName: sc.name,
      phaseLabel,
      stepIndex: Math.max(1, stepIndex),
      totalSteps: Math.max(1, totalWaitSteps),
      phaseEndTime: anchor + phaseWait * 1000,
      endTime: anchor + remaining * 1000,
    };
  }

  private computeNext(): void {
    const tasks = this.tasks().filter((t: any) => t.enabled);
    const now = new Date();
    const skipToday = this.skipToday;
    let best: { when: number; scenarioName: string } | null = null;

    for (let off = 0; off <= 7; off++) {
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + off);
      const wd = day.getDay();                                  // 0=Sun..6=Sat, matches tm_wday
      for (const t of tasks) {
        // dow is the weekday list (0=Sun..6=Sat). Empty means no days selected, which the
        // firmware scheduler treats as "never" — so such a task has no next run.
        const dow: number[] = t.dow || [];
        if (!dow.includes(wd)) continue;
        if (off === 0 && skipToday) continue;                   // watering skipped today

        const mins = t.time || 0;                               // minutes since midnight
        const when = new Date(day);
        when.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
        if (when.getTime() <= now.getTime()) continue;

        if (!best || when.getTime() < best.when)
          best = { when: when.getTime(), scenarioName: this.taskScenarioName(t) };
      }
    }
    this.nextRun = best ? { scenarioName: best.scenarioName, when: best.when } : null;
  }

  private taskScenarioName(t: any): string {
    const a = (t.actions || []).find((x: any) => x.type === 'scenario');
    if (a) {
      const s = this.scenarios().find((sc: any) => sc.id === a.scenarioId);
      return s?.name || `Scenario ${a.scenarioId}`;
    }
    return t.name || 'Task';
  }
}
